<template>
  <div class="sticky top-8">
    <UCard :ui="{ rounded: 'rounded-none md:rounded-lg' }">
      <div class="space-y-6">
        <!-- Özet Başlığı -->
        <h3 class="text-lg font-medium text-slate-900">Sipariş Özeti</h3>

        <!-- Fiyat Detayları -->
        <div class="space-y-4">
          <div class="flex justify-between">
            <span class="text-slate-600">Ara Toplam</span>
            <span class="font-medium">{{
              formatPrice(cartStore.subtotal)
              }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-slate-600">Kargo</span>
            <span class="font-medium text-orange-500" v-if="cartStore.shipping_fee === 0">
              Ücretsiz
            </span>
            <span class="font-medium" v-else>{{
              formatPrice(cartStore.shipping_fee)
              }}</span>
          </div>
          <div v-if="cartStore.discountTotal > 0" class="flex justify-between">
            <span class="text-slate-600">İndirim</span>
            <span class="font-medium text-orange-500">
              -{{ formatPrice(cartStore.discountTotal) }}
            </span>
          </div>
        </div>

        <!-- Toplam -->
        <div class="pt-6 border-t">
          <div class="flex justify-between items-center">
            <span class="text-base font-medium">Toplam</span>
            <span class="text-xl font-semibold text-orange-500">
              {{ formatPrice(cartStore.cartTotal) }}
            </span>
          </div>
        </div>

        <!-- Fiyat Değişikliği Uyarısı -->
        <UAlert v-if="cartStore.hasPriceChanges" color="amber" variant="soft" icon="i-heroicons-exclamation-triangle"
          :title="`Fiyat Değişikliği (${cartStore.priceChangedItemsCount} ürün)`" class="mb-4">
          <div class="flex items-center">
            <p>Sepetinizdeki bazı ürünlerin fiyatları değişmiştir. Lütfen sepetinizi gözden geçirin.</p>
            <UButton color="amber" variant="ghost" class="p-0 ml-1.5" :ui="{ rounded: 'rounded-full' }"
              @click="showPriceInfoModal = true">
              <UIcon name="i-heroicons-information-circle" class="w-4 h-4 text-amber-500 flex-shrink-0" />
            </UButton>
          </div>
        </UAlert>

        <!-- Stok Uyarısı -->
        <UAlert v-if="cartStore.hasOutOfStockItems" color="orange" variant="soft"
          icon="i-heroicons-exclamation-triangle" :title="`Stok Uyarısı (${cartStore.outOfStockItemsCount} ürün)`"
          class="mb-4 stock-alert">
          <div class="flex items-center">
            <p>Sepetinizde stok durumu yetersiz olan ürünler bulunmaktadır. Lütfen sepetinizi gözden geçirin.</p>
            <UButton color="orange" variant="ghost" class="p-0 ml-1.5" :ui="{ rounded: 'rounded-full' }"
              @click="showStockInfoModal = true">
              <UIcon name="i-heroicons-information-circle" class="w-4 h-4 text-orange-500 flex-shrink-0" />
            </UButton>
          </div>
        </UAlert>

        <!-- Ödeme Butonu -->
        <UButton color="orange" size="lg" @click="handleCheckout" block>
          Ödeme Yap
        </UButton>

        <!-- Alışverişe Devam Et -->
        <UButton color="sky" variant="soft" to="/" block>
          Alışverişe Devam Et
        </UButton>
      </div>
    </UCard>
  </div>

  <!-- Fiyat Değişikliği Bilgi Modalı -->
  <UModal v-model="showPriceInfoModal" :ui="{ width: 'sm:max-w-md' }">
    <UCard>
      <template #header>
        <div class="flex items-center">
          <UIcon name="i-heroicons-exclamation-triangle" class="w-5 h-5 text-amber-500 mr-2" />
          <h3 class="text-base font-medium">Fiyat Değişikliği Bilgisi</h3>
        </div>
      </template>
      <div class="text-sm text-slate-600 space-y-3">
        <p>
          Sepetinizdeki bazı ürünlerin fiyatları sepete eklediğiniz zamandan bu yana değişmiştir. Güncel fiyatlar
          üzerinden
          işlem yapılacaktır.
        </p>
        <div class="bg-amber-50 p-3 rounded-md">
          <div class="flex items-center justify-between">
            <span class="font-medium">Değişen Ürün Sayısı:</span>
            <span class="font-medium text-orange-500">{{ cartStore.priceChangedItemsCount }} ürün</span>
          </div>
          <div class="mt-2 text-xs">
            Fiyat değişikliği olan ürünlerin detaylarını görmek için ürün satırındaki bilgi ikonuna tıklayabilirsiniz.
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end">
          <UButton color="gray" variant="soft" @click="showPriceInfoModal = false">
            Kapat
          </UButton>
        </div>
      </template>
    </UCard>
  </UModal>

  <!-- Stok Uyarısı Bilgi Modalı -->
  <UModal v-model="showStockInfoModal" :ui="{ width: 'sm:max-w-md' }">
    <UCard>
      <template #header>
        <div class="flex items-center">
          <UIcon name="i-heroicons-exclamation-triangle" class="w-5 h-5 text-orange-500 mr-2" />
          <h3 class="text-base font-medium">Stok Uyarısı Bilgisi</h3>
        </div>
      </template>
      <div class="text-sm text-slate-600 space-y-3">
        <p>
          Bazı ürünlerin stok durumu değişmiş veya tükenmiştir. Stok durumu yetersiz olan ürünlerle ödeme işlemine devam
          edemezsiniz.
        </p>
        <div class="bg-orange-50 p-3 rounded-md">
          <div class="flex items-center justify-between">
            <span class="font-medium">Stok Sorunu Olan Ürün Sayısı:</span>
            <span class="font-medium text-orange-500">{{ cartStore.outOfStockItemsCount }} ürün</span>
          </div>
          <div class="mt-2 text-xs">
            Ödeme işlemine devam edebilmek için lütfen stok sorunu olan ürünleri sepetinizden çıkarın veya miktarlarını
            güncelleyin.
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end">
          <UButton color="gray" variant="soft" @click="showStockInfoModal = false">
            Kapat
          </UButton>
        </div>
      </template>
    </UCard>
  </UModal>
</template>

<script setup>
const cartStore = useCartStore()
const router = useRouter()
const showPriceInfoModal = ref(false)
const showStockInfoModal = ref(false)

const handleCheckout = () => {
  if (cartStore.hasOutOfStockItems) {
    // Stok uyarısına scroll yap
    const alertElement = document.querySelector('.stock-alert')
    if (alertElement) {
      alertElement.scrollIntoView({ behavior: 'smooth' })
    }
    return
  }

  router.push('/auth?callback=/odeme')
}
</script>
